// awsStorage — S3-backed drop-in for the subset of sb.storage moreece.com
// uses. All paths are routed through the moreece-storage-presign Lambda.
//
// storage_path convention: values stored in DB carry an `s3:` prefix so
// read helpers know the file lives in S3. Older Supabase-stored paths
// never landed in this RDS instance (migration 69 wiped placeholders;
// Phase 3 cutover started fresh), so we don't maintain a Supabase
// fallback branch.
//
// API mirrors sb.storage.from(bucket); getPublicUrl is async (returns a
// Promise) because signed URLs need a round-trip to the presign Lambda —
// all current callers already `await` it.

const PRESIGN_URL = import.meta.env.VITE_AWS_STORAGE_PRESIGN_URL;

function getSessionPin() {
  try {
    const raw = localStorage.getItem('fon-objectives-session');
    if (!raw) return null;
    return JSON.parse(raw)?.code || null;
  } catch { return null; }
}

async function presign(action, path, contentType) {
  if (!PRESIGN_URL) {
    throw new Error('VITE_AWS_STORAGE_PRESIGN_URL not configured');
  }
  const res = await fetch(PRESIGN_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action, path, contentType, userPin: getSessionPin() }),
  });
  if (!res.ok) {
    const j = await res.json().catch(() => ({}));
    throw new Error(j.error || `presign ${action} failed: ${res.status}`);
  }
  return res.json();
}

// Strip an `s3:` prefix if present; otherwise assume the caller passed a
// raw key (e.g. signed-URL previews) and pass through unchanged.
function s3Key(storagePath) {
  return typeof storagePath === 'string' && storagePath.startsWith('s3:')
    ? storagePath.slice(3)
    : storagePath;
}

export const storage = {
  from(bucket) {
    return {
      // Returns { data: { path }, error }. On success, data.path is the
      // value the caller should store in DB (already prefixed with `s3:`).
      async upload(path, file, options = {}) {
        const key = `${bucket}/${path}`;
        try {
          const { url } = await presign('upload', key, options.contentType || file.type);
          const putRes = await fetch(url, {
            method: 'PUT',
            headers: { 'Content-Type': options.contentType || file.type || 'application/octet-stream' },
            body: file,
          });
          if (!putRes.ok) return { error: { message: `S3 PUT failed: ${putRes.status}` }, data: null };
          return { data: { path: `s3:${key}` }, error: null };
        } catch (e) {
          return { error: { message: e.message || 'upload failed' }, data: null };
        }
      },

      // expiresIn kept for sb.storage compatibility; AWS uses its own
      // defaults per action.
      async createSignedUrl(storagePath /* expiresIn */) {
        try {
          const { url } = await presign('download', s3Key(storagePath));
          return { data: { signedUrl: url }, error: null };
        } catch (e) {
          return { error: { message: e.message || 'signed url failed' }, data: null };
        }
      },

      // Async in this shim (Supabase was sync). All callers already await.
      async getPublicUrl(storagePath) {
        try {
          const { url } = await presign('download', s3Key(storagePath));
          return { data: { publicUrl: url } };
        } catch {
          return { data: { publicUrl: '' } };
        }
      },

      async remove(storagePaths) {
        const arr = Array.isArray(storagePaths) ? storagePaths : [storagePaths];
        const errors = [];
        for (const sp of arr) {
          try { await presign('delete', s3Key(sp)); }
          catch (e) { errors.push(e.message || 'delete failed'); }
        }
        return errors.length ? { error: { message: errors.join('; ') } } : { error: null };
      },
    };
  },
};
